import { useMemo } from "react";
import { getRiskTagGroups, isFlowLimited } from "@/helpers";
import type { AmlHistoryItemData } from "@/store";
import { TxDirectionEnum, RISK_GROUPS } from "@/config";
import { RiskDetailsCategory } from "./RiskDetailsCategory";
// import bluredDanger from "@/assets/images/blured-danger.svg";

import styles from "./RiskSignalDetails.module.sass";

interface RiskSignalDetailsProps {
  item: AmlHistoryItemData;
}

export const RiskSignalDetails = ({ item }: RiskSignalDetailsProps) => {
  const groups = useMemo(
    () => getRiskTagGroups(item.signalTags),
    [item.signalTags],
  );

  const hasSignalValues = useMemo(() => {
    if (!item.signalTags) return false;

    const hasSignals = item.signalTags.length;
    const hasHighRisk = item?.hasHighRisk || false;

    return hasSignals || hasHighRisk;
  }, [item]);

  const signalTitle = useMemo(() => {
    if (hasSignalValues) {
      if (item?.tx) {
        return item.direction === TxDirectionEnum.DEPOSIT
          ? "Source of transferred funds"
          : "Destination address sending connections";
      }

      return "Risk Breakdown";
    }

    if (item?.tx) {
      if (item.signalTags) {
        const lowSignalsPresent = [
          ...RISK_GROUPS.suspicious,
          ...RISK_GROUPS.trusted,
        ].some((signal) => item.signalTags.includes(signal));

        if (!lowSignalsPresent) {
          return "";
        }
      }

      return item.direction === TxDirectionEnum.DEPOSIT
        ? "No significant connections between the source of transferred funds and risk factors were found"
        : "No significant connections between the destination address sendings and risk factors were found";
    }

    return "No significant connections between the address and risk factors were found";
  }, [item, hasSignalValues]);

  const showBluredBlock = useMemo(() => {
    if (!isFlowLimited(item.flow)) {
      return false;
    }
    return item?.hasHighRisk || false;
  }, [item]);

  const showDetails = useMemo(() => {
    if (Object.keys(groups).length > 0) {
      return true;
    }

    if (showBluredBlock) {
      return true;
    }
    return false;
  }, [showBluredBlock, groups]);

  return (
    <>
      <div className={styles.title}>{signalTitle}</div>

      {showDetails && (
        <div className={styles.list}>
          {Object.keys(groups).map((category) => (
            <RiskDetailsCategory
              key={category}
              category={category}
              risks={groups[category]}
            />
          ))}
        </div>
      )}
    </>
  );
};
